import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import {
  getEmployeesFromBuk,
  getBukPersonalHistoryDates,
  getBukPersonalHistoryByDate,
  syncBukPersonalNow,
  createBukPersonalRecord,
  updateBukPersonalRecord,
  deleteBukPersonalRecord
} from '../api/employeeApi';

// Registro histórico (incluye id y fecha del snapshot)
interface BukPersonalRecord {
    id?: number;
    snapshotDate?: string;
    rut: string;
    name: string;
    firstName: string;
    lastName: string;
    position?: string;
    costCenter?: string;
    status?: string;
    countryCode?: string;
}

type ViewMode = 'actual' | 'historico';

const emptyRecord: BukPersonalRecord = {
  rut: '', name: '', firstName: '', lastName: '',
  position: '', costCenter: '', countryCode: '', status: 'activo'
};

// Formatea una fecha ISO (YYYY-MM-DD) a formato local legible
const formatDateLabel = (isoDate: string): string => {
  const [y, m, d] = isoDate.split('-');
  return d && m && y ? `${d}-${m}-${y}` : isoDate;
};

const BukManager: React.FC = () => {
  const [mode, setMode] = useState<ViewMode>('actual');

  // Estado vista Actual
  const [loading, setLoading] = useState<boolean>(true);
  const [bukEmployees, setBukEmployees] = useState<BukPersonalRecord[]>([]);

  // Estado vista Histórico
  const [dates, setDates] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [historyEmployees, setHistoryEmployees] = useState<BukPersonalRecord[]>([]);
  const [syncing, setSyncing] = useState<boolean>(false);

  // Estado del diálogo de edición/creación
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [editing, setEditing] = useState<BukPersonalRecord>(emptyRecord);
  const [saving, setSaving] = useState<boolean>(false);

  // --- Vista Actual (memoria en tiempo real) ---
  const fetchBukData = async () => {
    setLoading(true);
    try {
      const data = await getEmployeesFromBuk();
      setBukEmployees(data);
    } catch (error) {
      console.error("Error al obtener datos en memoria de Buk", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBukData();
  }, []);

  // --- Vista Histórico: cargar fechas disponibles ---
  const fetchDates = async (autoSelect: boolean = true) => {
    try {
      const availableDates = await getBukPersonalHistoryDates();
      setDates(availableDates);
      if (autoSelect && availableDates.length > 0) {
        setSelectedDate((prev) => (prev && availableDates.includes(prev) ? prev : availableDates[0]));
      }
      return availableDates;
    } catch (error) {
      console.error("Error al obtener fechas del histórico", error);
      return [];
    }
  };

  useEffect(() => {
    if (mode !== 'historico') return;
    fetchDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // --- Vista Histórico: cargar personal de la fecha seleccionada ---
  const fetchHistory = async () => {
    if (!selectedDate) {
      setHistoryEmployees([]);
      return;
    }
    setHistoryLoading(true);
    try {
      const data = await getBukPersonalHistoryByDate(selectedDate);
      setHistoryEmployees(data);
    } catch (error) {
      console.error("Error al obtener personal histórico", error);
      setHistoryEmployees([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (mode !== 'historico') return;
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedDate]);

  // --- Sincronización manual (guarda snapshot de hoy) ---
  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      await syncBukPersonalNow();
      const availableDates = await fetchDates(false);
      // Selecciona la fecha más reciente (la de hoy recién guardada)
      if (availableDates.length > 0) {
        setSelectedDate(availableDates[0]);
      }
    } catch (error) {
      console.error("Error al sincronizar manualmente", error);
    } finally {
      setSyncing(false);
    }
  };

  // --- Diálogo: abrir para crear ---
  const handleOpenCreate = () => {
    setEditing({ ...emptyRecord, snapshotDate: selectedDate });
    setDialogOpen(true);
  };

  // --- Diálogo: abrir para editar ---
  const handleOpenEdit = (record: BukPersonalRecord) => {
    setEditing({ ...record });
    setDialogOpen(true);
  };

  const handleDialogChange = (field: keyof BukPersonalRecord, value: string) => {
    setEditing((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...editing, snapshotDate: editing.snapshotDate || selectedDate };
      if (editing.id) {
        await updateBukPersonalRecord(editing.id, payload);
      } else {
        await createBukPersonalRecord(payload);
      }
      setDialogOpen(false);
      await fetchDates(false);
      await fetchHistory();
    } catch (error) {
      console.error("Error al guardar el registro histórico", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record: BukPersonalRecord) => {
    if (!record.id) return;
    if (!window.confirm(`¿Eliminar el registro de ${record.name} ${record.firstName}?`)) return;
    try {
      await deleteBukPersonalRecord(record.id);
      await fetchHistory();
    } catch (error) {
      console.error("Error al eliminar el registro histórico", error);
    }
  };

  const handleModeChange = (_e: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode !== null) {
      setMode(newMode);
    }
  };

  const isHistorico = mode === 'historico';
  const displayedEmployees = isHistorico ? historyEmployees : bukEmployees;
  const isLoading = isHistorico ? historyLoading : loading;

  return (
    <Box sx={{ p: 2 }}>
      {/* Selector de modo Actual / Histórico */}
      <ToggleButtonGroup
        value={mode}
        exclusive
        onChange={handleModeChange}
        size="small"
        sx={{ mb: 2 }}
      >
        <ToggleButton value="actual">Actual</ToggleButton>
        <ToggleButton value="historico">Histórico</ToggleButton>
      </ToggleButtonGroup>

      {/* Cabecera con Contador y controles */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="body1" color="textSecondary">
          Total encontrados: {displayedEmployees.length}
        </Typography>

        {isHistorico ? (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="buk-history-date-label">Fecha</InputLabel>
              <Select
                labelId="buk-history-date-label"
                label="Fecha"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                disabled={dates.length === 0}
              >
                {dates.map((d) => (
                  <MenuItem key={d} value={d}>{formatDateLabel(d)}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              size="small"
              startIcon={<SyncIcon />}
              onClick={handleSyncNow}
              disabled={syncing}
            >
              {syncing ? <CircularProgress size={20} color="inherit" /> : 'Sincronizar hoy'}
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              disabled={!selectedDate}
            >
              Agregar
            </Button>
          </Stack>
        ) : (
          <Button
            variant="outlined"
            size="small"
            onClick={fetchBukData}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Recargar Tabla'}
          </Button>
        )}
      </Box>

      {/* Renderizado Condicional: Spinner vs Tabla */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 600, boxShadow: 3 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>RUT</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Nombre Completo</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Cargo</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', textAlign: 'center' }}>País</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', textAlign: 'center' }}>Centro Costo</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', textAlign: 'center' }}>Estado</TableCell>
                {isHistorico && (
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', textAlign: 'center' }}>Acciones</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedEmployees.length > 0 ? (
                displayedEmployees.map((emp, idx) => (
                  <TableRow key={emp.id ?? `${emp.rut}-${idx}`} hover>
                    <TableCell>{emp.rut}</TableCell>
                    <TableCell>{`${emp.name} ${emp.firstName} ${emp.lastName}`}</TableCell>
                    <TableCell>{emp.position || 'N/A'}</TableCell>

                    <TableCell align="center">
                      {emp.countryCode ? (
                          <Chip label={emp.countryCode} size="small" color="info" />
                      ) : '-'}
                    </TableCell>

                    <TableCell align="center">
                      {emp.costCenter ? (
                          <Chip label={emp.costCenter} size="small" color="secondary" variant="outlined" />
                      ) : '-'}
                    </TableCell>

                    <TableCell align="center">
                      <Chip
                        label={emp.status?.toUpperCase() || 'ACTIVO'}
                        size="small"
                        color="success"
                      />
                    </TableCell>

                    {isHistorico && (
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" color="primary" onClick={() => handleOpenEdit(emp)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => handleDelete(emp)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={isHistorico ? 7 : 6} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="textSecondary">
                      {isHistorico
                        ? (dates.length === 0
                            ? 'Aún no hay registros históricos guardados. Usa "Sincronizar hoy" para cargar los datos.'
                            : 'No hay personal registrado para la fecha seleccionada.')
                        : 'No se encontraron empleados activos.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Diálogo de creación / edición */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing.id ? 'Editar registro' : 'Agregar registro'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="RUT" size="small" fullWidth
              value={editing.rut}
              onChange={(e) => handleDialogChange('rut', e.target.value)}
            />
            <TextField
              label="Nombre" size="small" fullWidth
              value={editing.name}
              onChange={(e) => handleDialogChange('name', e.target.value)}
            />
            <TextField
              label="Apellido Paterno" size="small" fullWidth
              value={editing.firstName}
              onChange={(e) => handleDialogChange('firstName', e.target.value)}
            />
            <TextField
              label="Apellido Materno" size="small" fullWidth
              value={editing.lastName}
              onChange={(e) => handleDialogChange('lastName', e.target.value)}
            />
            <TextField
              label="Cargo" size="small" fullWidth
              value={editing.position || ''}
              onChange={(e) => handleDialogChange('position', e.target.value)}
            />
            <TextField
              label="Centro de Costo" size="small" fullWidth
              value={editing.costCenter || ''}
              onChange={(e) => handleDialogChange('costCenter', e.target.value)}
            />
            <TextField
              label="País" size="small" fullWidth
              value={editing.countryCode || ''}
              onChange={(e) => handleDialogChange('countryCode', e.target.value)}
            />
            <TextField
              label="Estado" size="small" fullWidth
              value={editing.status || ''}
              onChange={(e) => handleDialogChange('status', e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !editing.rut}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BukManager;
